import { FC, useState } from 'react';
import * as yup from 'yup';
import { Formik } from 'formik';
import { Form } from 'react-bootstrap';
import { ApolloError, useMutation } from '@apollo/client';
import Input from 'components/common/Input/Input';
import Button, { ButtonVariant } from 'components/common/Button/Button';
import Dialog from 'components/common/Dialog/Dialog';
import { GET_BILLING_AND_USAGE_DATA, REQUEST_EMAIL_CONFIRMATION, SET_PLAN } from 'components/billingAndUsage/queries';

import './BillingPlan.scss';

type PlanValues = {
  price: string;
  dailyGb: string;
  retentionDays: number;
};

type BillingPlanFormProps = {
  email: string;
  onAddCreditCard: () => void;
  onUpdate: () => void;
  handleError: (error: string) => void;
};

const initialValues: Partial<PlanValues> = {
  dailyGb: '',
  retentionDays: 0,
};

const initialPlanValues: PlanValues = {
  price: '',
  dailyGb: '',
  retentionDays: 0,
};

const retentionDays = [7, 15, 30, 60, 90];

const calculateRetentionDaysRatio = (retentionDaysValue: number): number => {
  switch (retentionDaysValue) {
    case 7:
      return 35;
    case 15:
      return 40;
    case 30:
      return 50;
    case 60:
      return 60;
    case 90:
      return 70;
    default:
      return 0;
  }
};

export const BillingPlanForm: FC<BillingPlanFormProps> = ({
  email,
  onAddCreditCard,
  onUpdate,
  handleError,
}: BillingPlanFormProps) => {
  const [planValues, setPlanValues] = useState<PlanValues>(initialPlanValues);
  const [showEmailConfirmation, setShowEmailConfirmation] = useState(false);
  const [showUpdatePlanConfirmation, setUpdatePlanConfirmation] = useState(false);
  const [showCustomPlanNotification, setCustomPlanNotification] = useState(false);

  const handleShowEmailConfirmation = (): void => {
    setShowEmailConfirmation(!showEmailConfirmation);
  };

  const handleShowUpdatePlanConfirmation = (): void => {
    setUpdatePlanConfirmation(!showUpdatePlanConfirmation);
  };

  const handleShowCustomPlanNotification = (): void => {
    setCustomPlanNotification(!showCustomPlanNotification);
  };

  const [resendEmail, { loading: resendLoading }] = useMutation(REQUEST_EMAIL_CONFIRMATION, {
    onCompleted: () => {
      handleShowEmailConfirmation();
    },
  });

  const [updatePlan, { loading: updateLoading }] = useMutation(SET_PLAN, {
    onCompleted: () => {
      handleShowUpdatePlanConfirmation();
      onUpdate();
    },
    onError: (error: ApolloError) => {
      if (error.message.includes('enter a credit card')) {
        onAddCreditCard();
      } else if (error.message.includes('confirm your e-mail')) {
        handleShowEmailConfirmation();
      } else {
        handleError(error.message);
      }
    },
    refetchQueries: [{ query: GET_BILLING_AND_USAGE_DATA }],
  });

  const validationSchema = yup.object({
    dailyGb: yup
      .number()
      .integer('Numeric digits only.')
      .min(1, 'Please enter a log volume in GB per day')
      .max(100, 'Please contact us at contact@scalyr.com for a custom plan.')
      .required('Please enter a log volume in GB per day.'),
    retentionDays: yup.number().required(),
  });

  const handleSubmit = (values: Partial<PlanValues>): void => {
    setPlanValues({ ...planValues, ...values });
    handleShowUpdatePlanConfirmation();
  };

  const handleUpdatePlan = (): void => {
    updatePlan({ variables: { dailyGb: planValues.dailyGb, retentionDays: planValues.retentionDays } });
  };

  const handleResendEmail = (): void => {
    resendEmail();
  };

  const handlePriceChange = (values: Partial<PlanValues>): void => {
    const price =
      !!values.dailyGb && !!values.retentionDays
        ? parseInt(values.dailyGb, 10) * calculateRetentionDaysRatio(values.retentionDays)
        : '0';
    setPlanValues({ ...planValues, price: price.toLocaleString() });
  };

  const updatePlanFooterButtons = {
    rightItems: [
      <Button variant={ButtonVariant.PRIMARY} onClick={handleUpdatePlan} disabled={updateLoading}>
        Update Plan
      </Button>,
      <Button onClick={handleShowUpdatePlanConfirmation} disabled={updateLoading}>
        Cancel
      </Button>,
    ],
  };

  const confirmEmailFooterButtons = {
    rightItems: [
      <Button variant={ButtonVariant.PRIMARY} onClick={handleResendEmail} disabled={resendLoading}>
        Resend Verification Email
      </Button>,
      <Button onClick={handleShowEmailConfirmation} disabled={resendLoading}>
        Cancel
      </Button>,
    ],
  };

  return (
    <>
      <Formik
        validationSchema={validationSchema}
        onSubmit={handleSubmit}
        initialValues={initialValues}
        validate={handlePriceChange}
      >
        {({ handleChange, values, errors, submitForm, setFieldValue }): JSX.Element => (
          <>
            <div className="BillingPlanForm__AverageLogWrapper">
              <div className="BillingPlanForm__AverageLog">
                <Input
                  label="Average Log Data per Day"
                  type="number"
                  name="dailyGb"
                  placeholder="10"
                  value={values.dailyGb?.toString()}
                  isInvalid={!!errors.dailyGb}
                  errorMessage={errors.dailyGb}
                  onChange={handleChange}
                  inputClassName="BillingPlanForm__AverageLog--Input"
                  labelClassName="BillingPlanForm__AverageLog--Field"
                  labelTextClassName="BillingPlanForm__AverageLog--Label"
                  errorTextClassName="BillingPlanForm__AverageLog--InvalidText"
                />
              </div>
              <span className="BillingPlanForm__AverageLog--SecondaryLabel">
                GB
                <button type="button" onClick={handleShowCustomPlanNotification} className="BillingPlanForm__Link">
                  Need more than 100 GB / day?
                </button>
              </span>
            </div>
            <div className="BillingPlanForm__KeepLogs">
              <Form.Label className="BillingPlanForm__KeepLogs--Label">Keep Logs for</Form.Label>
              {retentionDays.map((day: number) => (
                <Form.Check
                  inline
                  type="radio"
                  label={`${day} days`}
                  name="retentionDays"
                  checked={values.retentionDays === day}
                  onChange={(): void => {
                    setFieldValue('retentionDays', day);
                  }}
                  className="BillingPlanForm__KeepLogs--Radio"
                />
              ))}
              <button type="button" onClick={handleShowCustomPlanNotification} className="BillingPlanForm__Link">
                Want different number of days?
              </button>
            </div>
            <div className="BillingPlanForm__Footer">
              <p className="BillingPlanForm__Footer--Price">{`${planValues.price} $ / month`}</p>
              <Button
                variant={ButtonVariant.PRIMARY}
                onClick={submitForm}
                disabled={!!errors.dailyGb || !!errors.retentionDays}
              >
                Save Plan
              </Button>
            </div>
          </>
        )}
      </Formik>
      <Dialog
        title="Update Billing Settings"
        show={showUpdatePlanConfirmation}
        close={handleShowUpdatePlanConfirmation}
        footer={updatePlanFooterButtons}
      >
        {`Please confirm your choice of ${planValues.retentionDays} days retention and ${planValues.dailyGb} GB per day for ${planValues.price} /month`}
      </Dialog>
      <Dialog
        title="Email Verification Required"
        show={showEmailConfirmation}
        close={handleShowEmailConfirmation}
        footer={confirmEmailFooterButtons}
      >
        <span className="BillingPlanForm__EmailVerification">
          <p>
            Before completing this transaction, you must confirm your e-mail address ({email}
            ).
          </p>
          <p>You should have received an e-mail message titled &quot;Confirm your Scalyr account&quot;.</p>
          <p>Do you need a new copy of this e-mail to be sent?</p>
        </span>
      </Dialog>
      <Dialog title="Custom Plan" show={showCustomPlanNotification} close={handleShowCustomPlanNotification}>
        To discuss a new custom plan, please contact Support.
      </Dialog>
    </>
  );
};
